import { useState } from 'react';

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d68bfenmvg9ih2c79hng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-[#F8F6F3]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-2 h-2 bg-[#FF6B35] rounded-full"></div>
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Contact Us</span>
          </div>
          <h2 className="text-5xl font-bold text-[#2C2C2C] mb-4">Get Your Free Estimate</h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Ready to start your project? Send us a message today and we'll get back to you promptly
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#2C2C2C] mb-2">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#2C2C2C] mb-2">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#2C2C2C] mb-2">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                  placeholder="(702) 760-6213"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-[#2C2C2C] mb-2">Service Needed</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Pool Repair">Pool Repair</option>
                  <option value="Synthetic Grass">Synthetic Grass</option>
                  <option value="Pavers">Pavers</option>
                  <option value="Concrete Work">Concrete Work</option>
                  <option value="Landscaping">Landscaping</option>
                  <option value="Patio Construction">Patio Construction</option>
                  <option value="HVAC Services">HVAC Services</option>
                  <option value="Roofing">Roofing</option>
                  <option value="Water Filtration">Water Filtration</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#2C2C2C] mb-2">Project Details</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your project..."
                />
                <div className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF6B35] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#E55A2B] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  Thank you! We'll contact you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[#2C2C2C] mb-6">Contact Information</h3>
              
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#2C2C2C] mb-1">Phone</div>
                    <a href="tel:7027606213" className="text-gray-600 hover:text-[#FF6B35] transition-colors cursor-pointer">
                      702-760-6213
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#2C2C2C] mb-1">Email</div>
                    <a href="mailto:info@rwconstructionlv.com" className="text-gray-600 hover:text-[#FF6B35] transition-colors cursor-pointer">
                      info@rwconstructionlv.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#2C2C2C] mb-1">Location</div>
                    <div className="text-gray-600">
                      526 South 7th Street<br />
                      Las Vegas, NV 89101
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#2C2C2C] mb-1">Business Hours</div>
                    <div className="text-gray-600 text-sm">
                      Monday - Friday: 9:00 AM - 5:00 PM<br />
                      Saturday: By Appointment Only<br />
                      Sunday: Closed
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1877F2] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-facebook-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#2C2C2C] mb-1">Follow Us</div>
                    <a 
                      href="https://www.facebook.com/profile.php?id=61588126166404" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#1877F2] transition-colors cursor-pointer"
                    >
                      Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h4 className="text-xl font-bold text-[#2C2C2C] mb-4">Why Choose Us?</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <i className="ri-checkbox-circle-fill text-[#FF6B35] text-lg"></i>
                  Free estimates and consultations
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <i className="ri-checkbox-circle-fill text-[#FF6B35] text-lg"></i>
                  Licensed and insured contractors
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <i className="ri-checkbox-circle-fill text-[#FF6B35] text-lg"></i>
                  Quality workmanship guaranteed
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <i className="ri-checkbox-circle-fill text-[#FF6B35] text-lg"></i>
                  Competitive pricing
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <i className="ri-checkbox-circle-fill text-[#FF6B35] text-lg"></i>
                  Flexible financing options
                </li>
              </ul>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-sm h-[250px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3224.0876!2d-115.1391!3d36.1627!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c33c5e5e5e5e%3A0x5e5e5e5e5e5e5e5e!2s526%20S%207th%20St%2C%20Las%20Vegas%2C%20NV%2089101!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="RW Construction Las Vegas Location - 526 S 7th St, Las Vegas, NV 89101"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
